import React from 'react';
import { useTranslation } from 'react-i18next';
import { GetStartedSection } from './GetStartedSection';
import { NotificationsSection } from './NotificationsSection';
import { FilteredFinishYourSetupSection } from './FilteredFinishYourSetupSection';
import { calculateSetupStatus, getDefaultOwnCreated, getMissingItems } from '../../features/dashboard/utils/setupStatus';
import type { OwnCreatedDataResponse } from '../../features/dashboard/types/dashboard-data.types';
import { Card } from '../card';

export interface HomeConditionalLayoutProps {
  ownCreated: OwnCreatedDataResponse | null;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  /**
   * When true, hides the Get Started section (useful when it's shown elsewhere)
   */
  hideGetStarted?: boolean;
}

/**
 * Presentational layout that renders setup sections based on the admin's
 * content creation status owned by the dashboard feature.
 *
 * Data loading, error, and retry are provided by the caller (dashboard page);
 * this component only decides which sections to render.
 *
 * Business Logic:
 * - Scenario 1 (needs-get-started): 3-4 content types missing
 *   → Show: Get Started + Notifications (full width)
 *   → Note: Get Started can be shown above Analytics via hideGetStarted prop
 *
 * - Scenario 2 (needs-finish-setup): 1-2 content types missing
 *   → Show: Split Layout (Finish Your Setup + Notifications)
 *
 * - Scenario 3 (completed): All content types created
 *   → Show: Notifications only (full width)
 */
export function HomeConditionalLayout({
  ownCreated,
  isLoading,
  error,
  onRetry,
  hideGetStarted = false
}: HomeConditionalLayoutProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return <ConditionalLayoutSkeleton />;
  }

  if (error || !ownCreated) {
    return (
      <>
        <div
          className="w-full mt-10 flex flex-col items-center justify-center gap-3 rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center dark:border-gray-700 dark:bg-gray-800"
          role="alert"
        >
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {t('ui.something_went_wrong')}
          </p>
          {error ? (
            <p className="max-w-md text-sm text-gray-500 dark:text-gray-400">{error}</p>
          ) : null}
          <button
            type="button"
            onClick={onRetry}
            className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition-opacity hover:opacity-80 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
          >
            {t('ui.retry')}
          </button>
        </div>
        <div className="w-full mb-8 mt-10">
          <NotificationsSection />
        </div>
      </>
    );
  }

  // Calculate setup status based on content creation
  const setupStatus = calculateSetupStatus(ownCreated ?? getDefaultOwnCreated());
  const missingItemIds = getMissingItems(ownCreated);

  // Render appropriate layout based on status
  switch (setupStatus) {
    case 'needs-get-started':
      // Scenario 1: 3-4 content types missing → Show Get Started + Full Width Notifications
      // Note: Get Started may be hidden if shown above Analytics
      return (
        <>
          {!hideGetStarted && <GetStartedSection />}
          <div className="w-full mb-8 mt-10">
            <NotificationsSection />
          </div>
        </>
      );

    case 'needs-finish-setup':
      // Scenario 2: 1-2 content types missing → Show Split Layout with filtered items
      return (
        <div className="w-full mb-8 mt-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* Left: Finish Your Setup - showing only missing items */}
            <FilteredFinishYourSetupSection filterItemIds={missingItemIds} />

            {/* Right: Notifications */}
            <NotificationsSection />
          </div>
        </div>
      );

    case 'completed':
      // Scenario 3: All content types created → Show Notifications Only
      return (
        <div className="w-full mb-8 mt-10">
          <NotificationsSection />
        </div>
      );

    default:
      // Fallback (should never reach here)
      return (
        <div className="w-full mb-8">
          <NotificationsSection />
        </div>
      );
  }
}

/**
 * Loading skeleton component that matches the potential content structure
 */
const ConditionalLayoutSkeleton: React.FC = () => {
  return (
    <div className="w-full mb-8 mt-10">
      {/* Skeleton for potential sections */}
      <div className="animate-pulse">
        {/* Header skeleton */}
        <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-6" />

        {/* Content skeleton - could be Get Started cards or Activities Layout */}
        <Card className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-2xl">
          <div className="px-5 py-6 space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-40 h-20 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="flex-1 space-y-2">
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};
